import { useEffect, useState } from 'react';

import { StatusCode } from '../../../../../../api/enumerations';
import { updatePublicNotice } from '../../../../../../api/theHive/notices';
import { Constants } from '../../../../../../constants/hiveNotices';
import {
  extractYoutubeId,
  youtubeWatchUrl,
} from '../../../../../../helpers/youtube';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';

export interface UsePresentationVideoHook {
  url: string;
  setUrl: (value: string) => void;
  savedUrl: string | null;
  savedVideoId: string | null;
  editing: boolean;
  startEditing: () => void;
  cancelEditing: () => void;
  canSave: boolean;
  loading: boolean;
  handleSave: () => Promise<void>;
  handleRemove: () => Promise<void>;
}

interface UsePresentationVideoParams {
  noticeId: number;
  initialUrl: string | null;
  onSaved: (url: string | null) => void;
}

/**
 * Single optional YouTube link per notice. The backend validates and stores
 * the canonical `watch?v=<ID>` form; here we only pre-validate to avoid a
 * round trip for obviously wrong links, and mirror the canonical form after a
 * successful save so the preview matches what the proto-front will read.
 */
export function usePresentationVideo({
  noticeId,
  initialUrl,
  onSaved,
}: UsePresentationVideoParams): UsePresentationVideoHook {
  const [savedUrl, setSavedUrl] = useState<string | null>(initialUrl);
  const [url, setUrl] = useState(initialUrl ?? '');
  const [editing, setEditing] = useState(!initialUrl);
  const [loading, setLoading] = useState(false);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  useEffect(() => {
    if (initialUrl === savedUrl) return;

    setSavedUrl(initialUrl);
    setUrl(initialUrl ?? '');
    setEditing(!initialUrl);
  }, [initialUrl, savedUrl]);

  const savedVideoId = savedUrl ? extractYoutubeId(savedUrl) : null;
  const trimmedUrl = url.trim();
  const canSave =
    !loading && trimmedUrl.length > 0 && trimmedUrl !== (savedUrl ?? '');

  const patchVideo = async (value: string | null): Promise<boolean> => {
    if (loading) return false;

    setLoading(true);
    try {
      const response = await updatePublicNotice(noticeId, {
        presentation_video_url: value,
      });

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error(Constants.genericError);
      }
      return true;
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    const videoId = extractYoutubeId(trimmedUrl);
    if (!videoId) {
      handleSnackbar(Constants.invalidYoutubeUrl, true);
      return;
    }

    const canonical = youtubeWatchUrl(videoId);
    const saved = await patchVideo(trimmedUrl);
    if (!saved) return;

    setSavedUrl(canonical);
    setUrl(canonical);
    setEditing(false);
    onSaved(canonical);
    handleSnackbar(Constants.videoSaved, false);
  };

  const handleRemove = async (): Promise<void> => {
    const removed = await patchVideo(null);
    if (!removed) return;

    setSavedUrl(null);
    setUrl('');
    setEditing(true);
    onSaved(null);
    handleSnackbar(Constants.videoRemoved, false);
  };

  const startEditing = (): void => {
    setUrl(savedUrl ?? '');
    setEditing(true);
  };

  const cancelEditing = (): void => {
    setUrl(savedUrl ?? '');
    setEditing(false);
  };

  return {
    url,
    setUrl,
    savedUrl,
    savedVideoId,
    editing,
    startEditing,
    cancelEditing,
    canSave,
    loading,
    handleSave,
    handleRemove,
  };
}
